import type { ExtendedRow } from "./process-rows";

export function VllmSnippet(cfg: ExtendedRow) {
  return `import modal

#### begin configuration ####
## software config
VLLM_VERSION = "${cfg.framework_version}"
base_image = f"vllm/vllm-openai:{VLLM_VERSION}"

ENV_VARS = ${cfg.env_vars ? '{\n\t"' + cfg.env_vars.split("=").join('": "').split("\n").join('",\n\t"') + '",\n}' : "{}"}

CLI_ARGS = ${cfg.cli_args ? '[\n\t"' + cfg.cli_args.split(/\s+/).join('",\n\t"') + '",\n]' : "[]"}
${cfg.tokenizer ? 'CLI_ARGS += ["--tokenizer", "' + cfg.tokenizer + '"]' : ""}

## replica config
MAX_THROUGHPUT = ${Math.max(Math.floor(cfg.queries_per_second), 1)}
GPU = "${cfg.gpu_type}${cfg.gpu_count > 1 ? ":" + cfg.gpu_count : ""}"

## LLM config
MODEL_REPO = "${cfg.model_repo}"

#### end configuration ####

# setup

MODEL_CACHE_PATH = "/cache"
MINUTES = 60  # seconds
HOURS = 60 * MINUTES

image = (
	modal.Image.from_registry(base_image, add_python="3.13")
		.pip_install("hf-transfer", "grpclib", "requests")
		.env({"HF_HUB_CACHE": MODEL_CACHE_PATH, "HF_HUB_ENABLE_HF_TRANSFER": "1"})
		.dockerfile_commands(
			["RUN echo -n /usr/bin/python3 > /home/vllm-python", "ENTRYPOINT []"]
		)
)

hf_secret = modal.Secret.from_name("huggingface-secret")

model_cache_volume = modal.Volume.from_name("model-cache", create_if_missing=True)
model_cache = {MODEL_CACHE_PATH: model_cache_volume}

# deployment

app = modal.App("llm-engine")

command = " ".join(
	["/usr/bin/python3", "-m", "vllm.entrypoints.openai.api_server",
	"--model", MODEL_REPO]
	+ CLI_ARGS
)

@app.function(
	gpu=GPU,
	image=image,
	secrets=[hf_secret],
	volumes=model_cache,
	timeout=1 * HOURS,
)
@modal.web_server(port=8000, startup_timeout=1 * HOURS)
@modal.concurrent(max_inputs=100, target_inputs=MAX_THROUGHPUT)
def serve():
	import os
	import subprocess

	subprocess.Popen(command, env=os.environ | ENV_VARS, shell=True)\n\n${testingSuffix}`;
}

export function SglangSnippet(cfg: ExtendedRow) {
  return `import modal

#### begin configuration ####
## software config
SGLANG_VERSION = "${cfg.framework_version}"

ENV_VARS = ${cfg.env_vars ? '{\n\t"' + cfg.env_vars.split("=").join('": "').split("\n").join('",\n\t"') + '",\n}' : "{}"}

CLI_ARGS = ${cfg.cli_args ? '[\n\t"' + cfg.cli_args.split(/\s+/).join('",\n\t"') + '",\n]' : "[]"}
${cfg.tokenizer ? 'CLI_ARGS += ["--tokenizer-path", "' + cfg.tokenizer + '"]' : ""}

## replica config
MAX_THROUGHPUT = ${Math.max(Math.floor(cfg.queries_per_second), 1)}
GPU = "${cfg.gpu_type}${cfg.gpu_count > 1 ? ":" + cfg.gpu_count : ""}"

#  LLM config
MODEL_REPO = "${cfg.model_repo}"

#### end configuration ####

# setup

MODEL_CACHE_PATH = "/cache"
MINUTES = 60  # seconds
HOURS = 60 * MINUTES

image = (
	modal.Image.from_registry(
		f"lmsysorg/sglang:{SGLANG_VERSION}",
		setup_dockerfile_commands=["RUN ln -s /usr/bin/python3 /usr/bin/python"],
	)
	.pip_install(
		"hf-transfer",
		"grpclib",
		"requests",
		# install vllm for the Marlin kernels
		"vllm==0.8.4",  # but v0.8.5 incompatible with latest SGLang
	)
	.env({"HF_HUB_CACHE": MODEL_CACHE_PATH, "HF_HUB_ENABLE_HF_TRANSFER": "1"})
	.entrypoint([])
)

hf_secret = modal.Secret.from_name("huggingface-secret")

model_cache_volume = modal.Volume.from_name("model-cache", create_if_missing=True)
model_cache = {MODEL_CACHE_PATH: model_cache_volume}

# deployment

app = modal.App("llm-engine")

command = " ".join(
	["python", "-m", "sglang.launch_server",
	"--model-path", MODEL_REPO, "--host", "0.0.0.0"]
	+ CLI_ARGS
)

@app.function(
	gpu=GPU,
	image=image,
	secrets=[hf_secret],
	volumes=model_cache,
	timeout=1 * HOURS,
)
@modal.web_server(port=30000, startup_timeout=1 * HOURS)
@modal.concurrent(max_inputs=100, target_inputs=MAX_THROUGHPUT)
def serve():
	import os
	import subprocess

	subprocess.Popen(command, env=os.environ | ENV_VARS, shell=True)\n\n${testingSuffix}`;
}

export function TensorrtllmSnippet(cfg: ExtendedRow) {
  return `import modal

#### begin configuration ####
## software config
TENSORRT_LLM_VERSION = "${cfg.framework_version}"
CUDA_VERSION = "12.8.1"

ENV_VARS = ${cfg.env_vars ? '{\n\t"' + cfg.env_vars.split("=").join('": "').split("\n").join('",\n\t"') + '",\n}' : "{}"}

CLI_ARGS = ${cfg.cli_args ? '[\n\t"' + cfg.cli_args.split(/\s+/).join('",\n\t"') + '",\n]' : "[]"}
${cfg.tokenizer ? 'CLI_ARGS += ["--tokenizer", "' + cfg.tokenizer + '"]' : ""}

LLM_KWARGS = ${cfg.kwargs === "–" || cfg.kwargs == null ? "{}" : jsonToPyLiteral(cfg.kwargs)}

## replica config
MAX_THROUGHPUT = ${Math.max(Math.floor(cfg.queries_per_second), 1)}
GPU = "${cfg.gpu_type}${cfg.gpu_count > 1 ? ":" + cfg.gpu_count : ""}"

## LLM config
MODEL_REPO = "${cfg.model_repo}"

#### end configuration ####

# setup

MODEL_CACHE_PATH = "/cache"
MINUTES = 60  # seconds
HOURS = 60 * MINUTES

image = (
	modal.Image.from_registry(
		f"nvidia/cuda:{CUDA_VERSION}-devel-ubuntu24.04",
		add_python="3.12",
	)
	.entrypoint([])
	.apt_install(
		"openmpi-bin",
		"libopenmpi-dev",
		"git",
		"git-lfs",
		"wget"
	)
	.pip_install(
		f"tensorrt-llm=={TENSORRT_LLM_VERSION}",
		"pynvml",
		extra_index_url="https://pypi.nvidia.com",
	)
	.pip_install("hf-transfer", "huggingface_hub[hf_xet]", "requests")
	.env(
		{
			"HF_HUB_CACHE": MODEL_CACHE_PATH,
			"HF_HUB_ENABLE_HF_TRANSFER": "1",
			"PMIX_MCA_gds": "hash",
		}
	)
)

hf_secret = modal.Secret.from_name("huggingface-secret")

model_cache_volume = modal.Volume.from_name("model-cache", create_if_missing=True)
model_cache = {MODEL_CACHE_PATH: model_cache_volume}

LLM_KWARGS_PATH = "llm_kwargs.yaml"

# deployment

app = modal.App("llm-engine")

@app.cls(gpu=GPU, image=image, secrets=[hf_secret], volumes=model_cache, timeout=1 * HOURS)
@modal.concurrent(max_inputs=100, target_inputs=MAX_THROUGHPUT)
class TensorRTLLM:

	@modal.enter()
	def enter(self):
		import hashlib
		import json
		import os

		from huggingface_hub import snapshot_download
		from tensorrt_llm import LLM
		from tensorrt_llm.llmapi.llm_args import update_llm_args_with_extra_dict
		from tensorrt_llm.plugin import PluginConfig
		import tensorrt_llm
		import torch
		import yaml

		model_path = snapshot_download(MODEL_REPO)

		engine_fingerprint = hashlib.md5(
			json.dumps(LLM_KWARGS, sort_keys=True).encode()
		).hexdigest()
		print(f"Engine fingerprint: {engine_fingerprint}")

		self.engine_path = os.path.join(
			model_path,
			"tensorrt-llm-engines",
			f"{tensorrt_llm.__version__}-{MODEL_REPO}-{engine_fingerprint}",
		)
		print(f"Engine path: {self.engine_path}")

		if not os.path.exists(self.engine_path): # Build the engine
			llm_kwargs_simple = LLM_KWARGS.copy()

			# Build with plugins, but don't save them to the engine kwargs yaml
			# file, because trtllm-serve doesn't support loading them. This is
			# fine, since the plugins are incorporated at build time.
			if (
				"build_config" in LLM_KWARGS
				and "plugin_config" in LLM_KWARGS["build_config"]
			):
				LLM_KWARGS["build_config"]["plugin_config"] = (
					PluginConfig.from_dict(LLM_KWARGS["build_config"]["plugin_config"])
				)
				llm_kwargs_simple["build_config"].pop("plugin_config")

			# Prepare kwargs for LLM constructor
			final_kwargs = update_llm_args_with_extra_dict(
				{
					"model": MODEL_REPO,
					"tensor_parallel_size": torch.cuda.device_count(),
					"tokenizer": ${cfg.tokenizer ? '"' + cfg.tokenizer + '"' : "MODEL_REPO"}
				},
				LLM_KWARGS,
			)

			print(f"Building new engine at {self.engine_path}")
			llm = LLM(**final_kwargs)
			llm.save(self.engine_path)
			del llm

			with open(os.path.join(self.engine_path, LLM_KWARGS_PATH), "w") as f:
				yaml.dump(llm_kwargs_simple, f)

	@modal.web_server(port=8000, startup_timeout=1 * HOURS)
	def serve(self):
		import os
		import subprocess

		engine_path = self.engine_path if hasattr(self, "engine_path") else "none"

		command = " ".join(
			["trtllm-serve", engine_path, "--host", "0.0.0.0"]
			+ ["--extra_llm_api_options", os.path.join(engine_path, LLM_KWARGS_PATH)]
			+ CLI_ARGS
		)

		subprocess.Popen(command, env=os.environ | ENV_VARS, shell=True)

@app.local_entrypoint()
def test(test_timeout=30 * MINUTES):
	import json
	import time
	import urllib

	url = TensorRTLLM().serve.get_web_url()
	print(f"Running health check for server at {url}")
  print("Note: startup takes a while on the first two iterations, but is much faster after that.")
  print("On the first iteration with a new model, weights are downloaded at ~100 MB/s.")
  print("On the second iteration, a file read profile is recorded and used for future runs.")
	up, start, delay = False, time.time(), 10
	while not up:
		try:
			with urllib.request.urlopen(url + "/health") as response:
				up = response.getcode() == 200
		except Exception:
			if time.time() - start > test_timeout:
				break
			time.sleep(delay)

	assert up, f"Failed health check for server at {url}"

	print(f"Successful health check for server at {url}")

	prompt = "Testing! Is this thing on?"
	print(f"Sending a sample prompt to {url}", prompt, sep="\\n")

	headers = {"Content-Type": "application/json"}
	payload = json.dumps({"prompt": prompt, "max_tokens": 10, "model": MODEL_REPO})
	req = urllib.request.Request(
		url + "/v1/completions",
		data=payload.encode("utf-8"),
		headers=headers,
		method="POST",
	)
	with urllib.request.urlopen(req) as response:
		print(json.loads(response.read().decode()))`;
}

const testingSuffix = `# testing

@app.local_entrypoint()
def test(test_timeout=30 * MINUTES):
	import json
	import time
	import urllib

	url = serve.get_web_url()
	print(f"Running health check for server at {url}")
  print("Note: startup takes a while on the first two iterations, but is much faster after that.")
  print("On the first iteration with a new model, weights are downloaded at ~100 MB/s.")
  print("On the second iteration, a file read profile is recorded and used for future runs.")
	up, start, delay = False, time.time(), 10
	while not up:
		try:
			with urllib.request.urlopen(url + "/health") as response:
				up = response.getcode() == 200
		except Exception:
			if time.time() - start > test_timeout:
				break
			time.sleep(delay)

	assert up, f"Failed health check for server at {url}"

	print(f"Successful health check for server at {url}")

	messages = [{"role": "user", "content": "Testing! Is this thing on?"}]
	print(f"Sending a sample message to {url}", *messages, sep="\\n")

	headers = {"Content-Type": "application/json"}
	payload = json.dumps({"messages": messages, "model": MODEL_REPO, "max_tokens": 10})
	req = urllib.request.Request(
		url + "/v1/chat/completions",
		data=payload.encode("utf-8"),
		headers=headers,
		method="POST",
	)
	with urllib.request.urlopen(req) as response:
		print(json.loads(response.read().decode()))`;

function jsonToPyLiteral(jsonString: string, space = 4) {
  const o = JSON.parse(jsonString);
  let s = JSON.stringify(o, null, space);
  // swap JS keywords and properties to Python
  s = s
    .replace(/\bnull\b/g, "None")
    .replace(/\btrue\b/g, "True")
    .replace(/\bfalse\b/g, "False")
    .replace(/"([^"]+)":/g, '"$1":');
  return s;
}
